import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiEye, FiUser, FiSearch, FiAlertCircle, FiPlus, FiX, FiFileText, FiArrowUp, FiClock, FiDollarSign } from 'react-icons/fi';
import { fetchIndividualClients, IndividualClientData, updateIndividualClient } from '../../manage_clients/individual_clients/service/individualClientService';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import DeploymentModal from '../components/DeploymentModal';
import GuardDocumentsModal from '../components/GuardDocumentsModal';
import GuardTerminationModal from '../components/GuardTerminationModal';
import { clearAllDeployments, updateIndividualGuardSalaries } from '../service/deploymentService';
import { checkAllGuardsHaveDocuments, getGuardsWithDocuments } from '../service/documentService';

// Helper to get initials
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  
  @media (max-width: 768px) {
    overflow: visible; 
    height: auto;
    min-height: 100vh;
    display: block;
  }
`;

const MobileHeaderContainer = styled.div`
  position: sticky;
  top: 0;
  /* Full width fix matching BodyWrapper padding */
  width: calc(100% + ${theme.spacing[4]} * 2);
  margin-left: -${theme.spacing[4]};
  margin-right: -${theme.spacing[4]};
  background-color: ${theme.colors.light.background.primary};
  z-index: 40;
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
`;

const MobileActionRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: center;
  margin-top: ${theme.spacing[2]};
`;

const MobileCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[20]}; /* Space for FAB or bottom nav if any */
`;

const MobileCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.light.border.light};
  padding: ${theme.spacing[4]};
  box-shadow: ${theme.shadows.sm};
  transition: all 0.2s ease;
  cursor: pointer;

  &:active {
    transform: scale(0.98);
    background-color: ${theme.colors.light.background.secondary};
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing[3]};
`;

const MobileCardHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const MobileCardImage = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[600]};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: ${theme.typography.fontSize.lg};
  border: 1px solid ${theme.colors.primary[100]};
`;

const MobileCardTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.base};
`;

const MobileCardSubtitle = styled.div`
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  margin-top: 2px;
`;

const MobileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[2]} 0;
  border-bottom: 1px solid ${theme.colors.light.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const MobileStatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const MobileStatCard = styled.div`
  background: ${theme.colors.white};
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.light};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const MobileStatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
`;

const MobileStatLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  margin-top: 2px;
`;

const ScrollToTopButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${theme.colors.primary[600]};
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9999;
  transition: transform 0.2s, opacity 0.2s;
  pointer-events: auto;

  &:active {
    transform: scale(0.9);
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  align-items: center;
  flex-wrap: nowrap;
`;

const SearchInputContainer = styled.div`
  flex: 1;
  min-width: 250px;
  display: flex;
  align-items: stretch;
`;

const WarningBanner = styled.div`
  background-color: ${theme.colors.accent.warning}20;
  border: 1px solid ${theme.colors.accent.warning};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[3]};
  margin-top: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.accent.warning};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing[8]};
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid ${theme.colors.light.border.light};
    border-top-color: ${theme.colors.primary[500]};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[6]};
  flex-wrap: wrap;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const PaginationInfo = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.primary[500]};
  color: ${theme.colors.white};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};

  &:hover {
    background-color: ${theme.colors.primary[600]};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GuardCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const GuardItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.light};
  min-width: 150px;
`;

const GuardSlot = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  background: ${theme.colors.light.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  border: 1px dashed ${theme.colors.light.border.medium};
  min-width: 150px;
  color: ${theme.colors.light.text.muted};
  font-size: ${theme.typography.fontSize.sm};
`;

const GuardImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid ${theme.colors.light.border.medium};
`;

const GuardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const GuardName = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const GuardCNIC = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const StatusBadge = styled.span<{ variant?: 'complete' | 'incomplete' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant }) =>
    variant === 'complete' ? theme.colors.accent.success + '20' : theme.colors.accent.warning + '20'};
  color: ${({ variant }) =>
    variant === 'complete' ? theme.colors.accent.success : theme.colors.accent.warning};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  margin-bottom: ${theme.spacing[4]};
`;

const DetailLabel = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
`;

const DetailValue = styled.span`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
`;

const Badge = styled.span<{ variant?: 'active' | 'completed' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant }) =>
    variant === 'active' ? theme.colors.accent.success : theme.colors.light.background.tertiary};
  color: ${({ variant }) =>
    variant === 'active' ? theme.colors.white : theme.colors.light.text.secondary};
`;

// Helper to format date safely
const formatDeploymentDate = (dateVal: any) => {
  if (!dateVal) return '—';
  try {
    let date: Date;
    if (dateVal instanceof Date) {
      date = dateVal;
    } else if (typeof dateVal.toDate === 'function') {
      date = dateVal.toDate();
    } else if (dateVal.seconds) {
      date = new Date(dateVal.seconds * 1000);
    } else {
      date = new Date(dateVal);
    }

    if (isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '—';
  }
};

const HistoryCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  box-shadow: ${theme.shadows.sm};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary[200]};
  }
`;

const HistoryCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${theme.colors.light.background.tertiary};
  padding-bottom: ${theme.spacing[2]};
`;

const HistoryCardBody = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${theme.spacing[4]};
  align-items: center;
`;

const HistoryCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${theme.spacing[2]};
  border-top: 1px solid ${theme.colors.light.background.tertiary};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const GuardAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${theme.colors.light.background.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid ${theme.colors.primary[100]};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  color: ${theme.colors.light.text.primary};
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.light.background.secondary};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.md};
`;

interface DeploymentHistory {
  guard?: {
    id: string;
    name: string;
    guardId: string;
    cnic: string;
    image?: string;
  };
  postDate?: string;
  endDate?: string;
  status?: string;
}

interface ClientWithDeployment extends IndividualClientData {
  currentDeployment?: DeploymentHistory;
  history?: DeploymentHistory[];
  requiredGuard?: number;
  deployedGuards?: Array<{
    id: string;
    name: string;
    guardId: string;
    cnic: string;
    image?: string;
    slotNumber?: number;
    salary?: number;
  }>;
  hasIssue?: boolean;
  allGuardsHaveDocuments?: boolean;
  salary?: number;
  /** Per-guard salary (guardId -> amount). Used for attendance. */
  guardSalaries?: Record<string, number>;
  mobile?: string;
}

const IndividualDeploymentPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [clients, setClients] = useState<ClientWithDeployment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientWithDeployment | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [guardSalariesEdit, setGuardSalariesEdit] = useState<Record<string, number>>({});
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedClientForDocuments, setSelectedClientForDocuments] = useState<ClientWithDeployment | null>(null);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [clearingDeployments, setClearingDeployments] = useState(false);

  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [terminationGuard, setTerminationGuard] = useState<any>(null);
  const [terminationClientPath, setTerminationClientPath] = useState<string>('');
  const [initialDeploymentData, setInitialDeploymentData] = useState<{ path?: string; slot?: number; date?: Date } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [visibleItems, setVisibleItems] = useState(15);
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Check if user is a company owner/admin (not just staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail ||
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check deployment permissions
  const canViewDeployment = isCompanyOwner || (staffData?.access?.deployment === true);
  const canCreateDeployment = isCompanyOwner || (
    staffData?.access?.deployment === true &&
    (staffData?.permissions?.deployment === 'edit' || staffData?.permissions?.deployment === 'add' || staffData?.permissions?.deployment === 'delete')
  );

  // Use company ID if available, otherwise fall back to user UID (same as service)
  const companyId = companyData?.id || auth.currentUser?.uid || '';

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, companyData]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Ensure we have a valid company ID (same logic as service)
      const validCompanyId = companyId || auth.currentUser?.uid;
      if (!validCompanyId) {
        throw new Error('Company ID or user authentication required');
      }

      const clientsData = await fetchIndividualClients(validCompanyId);

      // Fetch deployment data for each client
      const clientsWithDeployment = await Promise.all(
        clientsData.map(async (client) => {
          const clientRef = doc(db, 'Individual', validCompanyId, 'clients', client.id);
          const clientSnap = await getDoc(clientRef);
          const clientData = clientSnap.data();

          const requiredGuard = clientData?.requiredGuard || 1;
          const currentDeployment = clientData?.currentDeployment || null;
          const history = clientData?.history || [];

          // Extract deployed guards from currentDeployment - normalize to array
          const guard = currentDeployment?.guard || null;
          const deployedGuards = Array.isArray(guard) ? guard : guard ? [guard] : [];

          // Check if client has deployment issue (missing guards)
          const hasIssue = deployedGuards.length < requiredGuard;

          // Check if all guards have documents
          let allGuardsHaveDocuments = false;
          if (deployedGuards.length > 0) {
            try {
              const validCompanyId = companyId || auth.currentUser?.uid || '';
              allGuardsHaveDocuments = await checkAllGuardsHaveDocuments(`Individual/${validCompanyId}/clients/${client.id}`);
            } catch (error) {
              console.error('Error checking documents:', error);
            }
          }

          // Merge per-guard salaries from client with deployed guards (guard object may have salary from client doc)
          const guardSalaries = clientData?.guardSalaries || {};
          const deployedWithSalary = deployedGuards.map((g: any) => ({
            ...g,
            salary: guardSalaries[g.id] ?? g.salary ?? 0,
          }));

          return {
            ...client,
            requiredGuard,
            currentDeployment,
            history,
            deployedGuards: deployedWithSalary,
            hasIssue,
            allGuardsHaveDocuments,
            slotEndDates: clientData?.slotEndDates || {},
            salary: clientData?.salary || 0,
            guardSalaries,
            mobile: clientData?.mobile || '',
          } as ClientWithDeployment;
        })
      );

      setClients(clientsWithDeployment);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error(error.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return clients;
    const term = searchTerm.toLowerCase();
    return clients.filter((c) => {
      const name = c.name?.toLowerCase() || '';
      const address = c.address?.toLowerCase() || '';
      return name.includes(term) || address.includes(term);
    });
  }, [clients, searchTerm]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleItems((prev) => prev + 15);
        }
      },
      { threshold: 1.0 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loading, filteredClients.length]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
    setVisibleItems(15);
  }, [searchTerm]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalClients = filteredClients.length;
    const totalGuardsDeployed = filteredClients.reduce((sum, c) => sum + (c.deployedGuards?.length || 0), 0);
    const clientsWithIssues = filteredClients.filter((c) => c.hasIssue).length;
    const totalRequiredGuards = filteredClients.reduce((sum, c) => sum + (c.requiredGuard || 1), 0);

    return {
      totalClients,
      totalGuardsDeployed,
      clientsWithIssues,
      totalRequiredGuards,
    };
  }, [filteredClients]);

  const handleViewHistory = (client: ClientWithDeployment) => {
    setSelectedClient(client);
    setShowHistoryModal(true);
  };

  const handleSlotClick = (client: ClientWithDeployment, slotIndex: number, guard: any | null) => {
    if (!canCreateDeployment) return;

    const clientPath = `Individual/${companyId}/clients/${client.id}`;

    if (guard) {
      setTerminationGuard(guard);
      setTerminationClientPath(clientPath);
      setInitialDeploymentData({ path: clientPath, slot: slotIndex });
      setShowTerminationModal(true);
    } else {
      let suggestedDate = new Date();
      const slotEndDates = (client as any).slotEndDates;
      if (slotEndDates && slotEndDates[slotIndex + 1]) {
        const lastDate = slotEndDates[slotIndex + 1];
        suggestedDate = new Date(lastDate.seconds ? lastDate.seconds * 1000 : lastDate);
        suggestedDate.setDate(suggestedDate.getDate() + 1);
      }

      setInitialDeploymentData({
        path: clientPath,
        slot: slotIndex,
        date: suggestedDate
      });
      setShowDeploymentModal(true);
    }
  };

  const handleTerminated = (endDate: Date) => {
    const nextDate = new Date(endDate);
    nextDate.setDate(nextDate.getDate() + 1);

    setInitialDeploymentData(prev => ({
      ...prev,
      date: nextDate
    }));

    loadData();
    setShowDeploymentModal(true);
  };

  // Render guards - show all required guard slots
  const renderGuards = (client: ClientWithDeployment) => {
    const requiredGuard = client.requiredGuard || 1;
    const deployedGuards = client.deployedGuards || [];
    const guards: React.ReactNode[] = [];

    for (let i = 0; i < requiredGuard; i++) {
      const slotNumber = i + 1;
      const slotIndex = i;

      // Find guard assigned to this specific slot number
      const guard = deployedGuards.find((g: any) => g.slotNumber === slotNumber);

      if (guard) {
        guards.push(
          <GuardItem
            key={i}
            onClick={() => handleSlotClick(client, slotIndex, guard)}
            style={{ cursor: canCreateDeployment ? 'pointer' : 'default' }}
          >
            {guard.image ? (
              <GuardImage
                src={guard.image}
                alt={guard.name}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <FiUser size={20} style={{ color: theme.colors.light.text.muted }} />
            )}
            <GuardInfo>
              <GuardName>{guard.name || guard.guardId || '—'}</GuardName>
              <GuardCNIC>{guard.cnic || '—'}</GuardCNIC>
            </GuardInfo>
          </GuardItem>
        );
      } else {
        guards.push(
          <GuardSlot
            key={i}
            onClick={() => handleSlotClick(client, slotIndex, null)}
            style={{ cursor: canCreateDeployment ? 'pointer' : 'default' }}
          >
            <FiUser size={20} style={{ color: theme.colors.light.text.muted }} />
            <span>Empty Slot</span>
          </GuardSlot>
        );
      }
    }

    return <GuardCell>{guards}</GuardCell>;
  };

  const clientsWithIssues = filteredClients.filter((c) => c.hasIssue);
  const clientsWithIssuesCount = clientsWithIssues.length;

  // Pagination
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredClients.slice(startIndex, endIndex);
  }, [filteredClients, currentPage]);

  const totalPages = Math.ceil(filteredClients.length / pageSize);

  const deploymentClients = useMemo(() => {
    return filteredClients.map((client) => ({
      id: client.id,
      name: client.name || '',
      type: 'Individual' as const,
      address: client.address,
      path: `Individual/${companyId}/clients/${client.id}`,
      deployedGuards: client.deployedGuards,
      requiredGuard: client.requiredGuard,
    }));
  }, [filteredClients, companyId]);

  // Show permission denied message if user doesn't have view access
  if (!canViewDeployment) {
    return (
      <DashboardLayout>
        <div style={{
          textAlign: 'center',
          padding: theme.spacing[12],
          color: theme.colors.light.text.secondary
        }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to view deployment pages.</p>
        </div>
      </DashboardLayout>
    );
  }

  // Mobile Render Logic
  if (isMobile) {
    return (
      <DashboardLayout>
        <PageContainer>
          <MobileHeaderContainer>
            <SearchInputContainer>
              <div style={{ position: 'relative', width: '100%' }}>
                <Input
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  dark
                  fullWidth
                />
              </div>
              <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: theme.colors.light.text.muted }}>
                <FiSearch size={20} />
              </div>
            </SearchInputContainer>

            <MobileActionRow>
              {canCreateDeployment && (
                <Button
                  onClick={() => setShowDeploymentModal(true)}
                  fullWidth
                  style={{ justifyContent: 'center', height: '48px', fontSize: theme.typography.fontSize.base }}
                >
                  <FiPlus size={20} style={{ marginRight: theme.spacing[2] }} />
                  Deploy Guards
                </Button>
              )}
            </MobileActionRow>
          </MobileHeaderContainer >

          <MobileCardContainer>
            <div style={{ padding: `0 ${theme.spacing[4]}` }}>
              <MobileStatGrid>
                <MobileStatCard>
                  <MobileStatValue>{summaryStats.totalClients}</MobileStatValue>
                  <MobileStatLabel>Clients</MobileStatLabel>
                </MobileStatCard>
                <MobileStatCard>
                  <MobileStatValue color={theme.colors.primary[600]}>{summaryStats.totalGuardsDeployed}</MobileStatValue>
                  <MobileStatLabel>Guards</MobileStatLabel>
                </MobileStatCard>
                <MobileStatCard>
                  <MobileStatValue color={theme.colors.accent.warning}>{summaryStats.clientsWithIssues}</MobileStatValue>
                  <MobileStatLabel>Issues</MobileStatLabel>
                </MobileStatCard>
                <MobileStatCard>
                  <MobileStatValue color={theme.colors.accent.info}>{summaryStats.totalRequiredGuards}</MobileStatValue>
                  <MobileStatLabel>Required</MobileStatLabel>
                </MobileStatCard>
              </MobileStatGrid>

              {clientsWithIssuesCount > 0 && (
                <WarningBanner>
                  <FiAlertCircle size={20} style={{ flexShrink: 0 }} />
                  <span>
                    {clientsWithIssuesCount} issue{clientsWithIssuesCount > 1 ? 's' : ''} found
                  </span>
                </WarningBanner>
              )}

              {filteredClients.slice(0, visibleItems).map((client) => (
                <MobileCard key={client.id} onClick={() => handleViewHistory(client)}>
                  <MobileCardHeader>
                    <MobileCardHeaderContent>
                      <MobileCardImage>{getInitials(client.name)}</MobileCardImage>
                      <div>
                        <MobileCardTitle>{client.name}</MobileCardTitle>
                        <MobileCardSubtitle>{client.address}</MobileCardSubtitle>
                        <MobileCardSubtitle>{client.mobile}</MobileCardSubtitle>
                        <MobileCardSubtitle style={{ color: theme.colors.accent.success, fontWeight: 'bold' }}>
                          {client.deployedGuards?.length
                            ? client.deployedGuards.map((g: any) => `${g.name || g.guardId}: Rs.${((client.guardSalaries?.[g.id] ?? g.salary ?? 0)).toLocaleString()}`).join(' · ')
                            : 'Rs. 0'}
                        </MobileCardSubtitle>
                      </div>
                    </MobileCardHeaderContent>
                    <StatusBadge variant={client.hasIssue || !client.allGuardsHaveDocuments ? 'incomplete' : 'complete'}>
                      {client.hasIssue ? 'Incomplete' : !client.allGuardsHaveDocuments ? 'Pending' : 'Deployed'}
                    </StatusBadge>
                  </MobileCardHeader>

                  <MobileCardBody>
                    <div style={{ marginTop: theme.spacing[2] }}>
                      <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing[2] }}>
                        Deployed Guards ({client.deployedGuards?.length || 0}/{client.requiredGuard || 1})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
                        {(() => {
                          const required = client.requiredGuard || 1;
                          const deployed = client.deployedGuards || [];
                          const items = [];
                          for (let i = 0; i < required; i++) {
                            const slotNumber = i + 1;
                            const slotIndex = i;

                            // Find guard assigned to this specific slot number
                            const g = deployed.find((guard: any) => guard.slotNumber === slotNumber);

                            if (g) {
                              items.push(
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                                  {g.image ? (
                                    <img src={g.image} alt={g.name} style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                                  ) : (
                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: theme.colors.light.background.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <FiUser size={12} color={theme.colors.light.text.muted} />
                                    </div>
                                  )}
                                  <span style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.bold, color: '#000000' }}>
                                    {g.name} <span style={{ fontWeight: 'normal', color: theme.colors.light.text.secondary }}>({g.guardId})</span>
                                  </span>
                                </div>
                              );
                            } else {
                              items.push(
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], opacity: 0.5 }}>
                                  <div style={{ width: 24, height: 24, borderRadius: '50%', border: `1px dashed ${theme.colors.light.border.medium}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FiUser size={12} color={theme.colors.light.text.muted} />
                                  </div>
                                  <span style={{ fontSize: theme.typography.fontSize.xs, fontStyle: 'italic', color: theme.colors.light.text.muted }}>
                                    Empty Slot
                                  </span>
                                </div>
                              );
                            }
                          }
                          return items;
                        })()}
                      </div>
                    </div>
                  </MobileCardBody>
                </MobileCard>
              ))}

              {visibleItems < filteredClients.length && (
                <div ref={loadMoreRef} style={{ padding: '20px', textAlign: 'center' }}>
                  <span style={{ color: theme.colors.light.text.secondary }}>Loading more...</span>
                </div>
              )}
              {filteredClients.length === 0 && !loading && (
                <EmptyState>
                  <EmptyStateText>No clients found.</EmptyStateText>
                </EmptyState>
              )}
            </div>
          </MobileCardContainer>

          {/* Modals Reuse */}
          <Modal
            isOpen={showHistoryModal}
            onClose={() => {
              setShowHistoryModal(false);
              setSelectedClient(null);
            }}
            title={`Deployment History`}
            size="lg"
            footer={
              <Button variant="secondary" onClick={() => {
                setShowHistoryModal(false);
                setSelectedClient(null);
              }}>
                Close
              </Button>
            }
          >
            {selectedClient && (
              <>
                {selectedClient.currentDeployment && (
                  <DetailGroup>
                    <DetailLabel>Current Deployment</DetailLabel>
                    <div style={{ padding: theme.spacing[3], background: theme.colors.light.background.secondary, borderRadius: theme.borderRadius.md }}>
                      {selectedClient.currentDeployment.guard && (
                        <>
                          <DetailValue>
                            <strong>Guard:</strong> {selectedClient.currentDeployment.guard.name || selectedClient.currentDeployment.guard.guardId || '—'}
                          </DetailValue>
                          <DetailValue>
                            <strong>CNIC:</strong> {selectedClient.currentDeployment.guard.cnic || '—'}
                          </DetailValue>
                        </>
                      )}
                      <DetailValue>
                        <strong>Status:</strong> {selectedClient.currentDeployment.status}
                      </DetailValue>
                    </div>
                  </DetailGroup>
                )}
                <DetailGroup>
                  <DetailLabel>Deployed Guards</DetailLabel>
                  {selectedClient.deployedGuards && selectedClient.deployedGuards.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
                      {selectedClient.deployedGuards.map((g, idx) => (
                        <div key={idx} style={{ padding: theme.spacing[2], border: `1px solid ${theme.colors.light.border.light}`, borderRadius: theme.borderRadius.md }}>
                          <div><strong>{g.name}</strong> ({g.guardId})</div>
                          <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>{g.cnic}</div>
                        </div>
                      ))}
                    </div>
                  ) : <div>No guards deployed</div>}
                </DetailGroup>
              </>
            )}
          </Modal>

          {
            showDeploymentModal && (
              <DeploymentModal
                isOpen={showDeploymentModal}
                onClose={() => setShowDeploymentModal(false)}
                clients={deploymentClients}
                clientType="Individual"
                onDeploymentSaved={() => {
                  loadData();
                  setShowDeploymentModal(false);
                }}
              />
            )
          }

          {
            showDocumentsModal && selectedClientForDocuments && (
              <GuardDocumentsModal
                isOpen={showDocumentsModal}
                onClose={() => {
                  setShowDocumentsModal(false);
                  setSelectedClientForDocuments(null);
                }}
                clientPath={`Individual/${companyId}/clients/${selectedClientForDocuments.id}`}
                clientName={`${selectedClientForDocuments.name}`}
              />
            )
          }

          {
            showScrollTop && (
              <ScrollToTopButton onClick={scrollToTop}>
                <FiArrowUp size={24} />
              </ScrollToTopButton>
            )
          }
        </PageContainer >
      </DashboardLayout >
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Individual Deployment</PageTitle>
        {canViewDeployment && (
          <div style={{ display: 'flex', gap: theme.spacing[2] }}>
            {/* Clear All Deployments Button - Commented for future use */}
            {/* <Button variant="secondary" onClick={() => setShowClearConfirmModal(true)}>
              <FiX size={18} style={{ marginRight: theme.spacing[2] }} />
              Clear All Deployments
            </Button> */}
            {canCreateDeployment && (
              <Button onClick={() => setShowDeploymentModal(true)}>
                <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
                Deploy Guards
              </Button>
            )}
          </div>
        )}
      </PageHeader>

      {/* Summary Cards */}
      <StatsContainer>
        <StatCard>
          <CardBody>
            <StatValue>{summaryStats.totalClients}</StatValue>
            <StatLabel>Total Clients</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.primary[600]}>{summaryStats.totalGuardsDeployed}</StatValue>
            <StatLabel>Guards Deployed</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.warning}>{summaryStats.clientsWithIssues}</StatValue>
            <StatLabel>Clients with Issues</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.info}>{summaryStats.totalRequiredGuards}</StatValue>
            <StatLabel>Total Required Guards</StatLabel>
          </CardBody>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          <SearchContainer>
            <SearchInputContainer>
              <Input
                placeholder="Search by name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                dark
                fullWidth
              />
            </SearchInputContainer>
          </SearchContainer>

          {clientsWithIssuesCount > 0 && (
            <WarningBanner>
              <FiAlertCircle size={20} />
              <span>
                {clientsWithIssuesCount} client{clientsWithIssuesCount > 1 ? 's have' : ' has'} guard deployment issues.
                Please review and deploy additional guards as needed.
              </span>
            </WarningBanner>
          )}

          {!loading && filteredClients.length === 0 && searchTerm && (
            <EmptyState>
              <EmptyStateText>No clients found matching your search.</EmptyStateText>
            </EmptyState>
          )}

          {loading ? (
            <Loader />
          ) : filteredClients.length > 0 ? (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Client Name</Table.HeaderCell>
                  <Table.HeaderCell>Address</Table.HeaderCell>
                  <Table.HeaderCell>Required Guards</Table.HeaderCell>
                  <Table.HeaderCell>Deployed Guards</Table.HeaderCell>
                  <Table.HeaderCell>Guard Salaries (Rs.)</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {paginatedClients.map((client) => (
                  <Table.Row key={client.id}>
                    <Table.Cell>{client.name || '—'}</Table.Cell>
                    <Table.Cell>{client.address || '—'}</Table.Cell>
                    <Table.Cell>{client.requiredGuard || 1}</Table.Cell>
                    <Table.Cell>
                      {renderGuards(client)}
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {client.deployedGuards?.length
                          ? client.deployedGuards.map((g: any) => (
                              <span key={g.id} style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.primary }}>
                                {g.name || g.guardId}: Rs. {(client.guardSalaries?.[g.id] ?? g.salary ?? 0).toLocaleString()}
                              </span>
                            ))
                          : <span style={{ color: theme.colors.light.text.muted }}>—</span>}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge variant={client.hasIssue || !client.allGuardsHaveDocuments ? 'incomplete' : 'complete'}>
                        {client.hasIssue ? 'Incomplete' : !client.allGuardsHaveDocuments ? 'Pending Documents' : 'Deployed'}
                      </StatusBadge>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
                        {client.deployedGuards && client.deployedGuards.length > 0 && (
                          <ActionButton
                            onClick={() => {
                              setSelectedClientForDocuments(client);
                              setShowDocumentsModal(true);
                            }}
                            title={client.allGuardsHaveDocuments ? "View Deployment Documents (PDF)" : "Attach Deployment Documents (PDF)"}
                            style={{
                              backgroundColor: !client.allGuardsHaveDocuments ? theme.colors.accent.warning : 'transparent',
                              color: !client.allGuardsHaveDocuments ? theme.colors.white : theme.colors.light.text.secondary,
                            }}
                          >
                            <FiFileText size={18} />
                          </ActionButton>
                        )}
                        <ActionButton
                          onClick={() => {
                            setSelectedClient(client);
                            const initial: Record<string, number> = {};
                            client.deployedGuards?.forEach((g: any) => {
                              initial[g.id] = client.guardSalaries?.[g.id] ?? g.salary ?? 0;
                            });
                            setGuardSalariesEdit(initial);
                            setShowSalaryModal(true);
                          }}
                          title="Set guard salaries"
                          style={{ backgroundColor: theme.colors.accent.info, color: theme.colors.white }}
                        >
                          <FiDollarSign size={18} />
                        </ActionButton>
                        <ActionButton
                          onClick={() => handleViewHistory(client)}
                          title="View History"
                        >
                          <FiEye size={18} />
                        </ActionButton>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <EmptyState>
              <EmptyStateText>No clients found.</EmptyStateText>
            </EmptyState>
          )}

          {!loading && filteredClients.length > 0 && (
            <PaginationContainer>
              <PaginationInfo>
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredClients.length)} of {filteredClients.length} clients
              </PaginationInfo>
              <PaginationControls>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <PaginationInfo>
                  Page {currentPage} of {totalPages}
                </PaginationInfo>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </PaginationControls>
            </PaginationContainer>
          )}
        </CardBody>
      </Card>

      {/* History Modal */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setSelectedClient(null);
        }}
        title={`Deployment History - ${selectedClient?.name || 'Client'}`}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => {
            setShowHistoryModal(false);
            setSelectedClient(null);
          }}>
            Close
          </Button>
        }
      >
        {selectedClient && (
          <div style={{ padding: theme.spacing[2] }}>
            {selectedClient.currentDeployment && (
              <DetailGroup>
                <DetailLabel style={{ marginBottom: theme.spacing[2], display: 'block' }}>Current Deployment</DetailLabel>
                <HistoryCard style={{ borderLeft: `4px solid ${theme.colors.accent.success}` }}>
                  <HistoryCardHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                      <FiUser color={theme.colors.primary[500]} />
                      <span style={{ fontWeight: theme.typography.fontWeight.bold, color: theme.colors.light.text.primary }}>
                        Active Guard
                      </span>
                    </div>
                    <Badge variant="active">Active</Badge>
                  </HistoryCardHeader>
                  <HistoryCardBody>
                    <GuardAvatar>
                      {selectedClient.currentDeployment.guard?.image ? (
                        <img src={selectedClient.currentDeployment.guard.image} alt="Guard" />
                      ) : (
                        <FiUser size={24} color={theme.colors.light.text.muted} />
                      )}
                    </GuardAvatar>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <div style={{ fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.base }}>
                        {selectedClient.currentDeployment.guard?.name || '—'}
                      </div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                        CNIC: {selectedClient.currentDeployment.guard?.cnic || '—'}
                      </div>
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.primary[600] }}>
                        ID: {selectedClient.currentDeployment.guard?.guardId || '—'}
                      </div>
                    </div>
                  </HistoryCardBody>
                  <HistoryCardFooter>
                    <DateBadge>
                      <FiClock size={14} />
                      Deployed: {formatDeploymentDate(selectedClient.currentDeployment.postDate)}
                    </DateBadge>
                  </HistoryCardFooter>
                </HistoryCard>
              </DetailGroup>
            )}

            {selectedClient.history && selectedClient.history.length > 0 && (
              <DetailGroup style={{ marginTop: theme.spacing[6] }}>
                <DetailLabel style={{ marginBottom: theme.spacing[2], display: 'block' }}>Previous Deployments</DetailLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[2] }}>
                  {selectedClient.history.map((deployment, index) => (
                    <HistoryCard key={index}>
                      <HistoryCardHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                          <FiUser color={theme.colors.light.text.muted} />
                          <span style={{ fontWeight: theme.typography.fontWeight.medium, color: theme.colors.light.text.secondary }}>
                            Past Deployment
                          </span>
                        </div>
                        <Badge variant="completed">Completed</Badge>
                      </HistoryCardHeader>
                      <HistoryCardBody>
                        <GuardAvatar>
                          {deployment.guard?.image ? (
                            <img src={deployment.guard.image} alt="Guard" />
                          ) : (
                            <FiUser size={24} color={theme.colors.light.text.muted} />
                          )}
                        </GuardAvatar>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ fontWeight: theme.typography.fontWeight.bold, fontSize: theme.typography.fontSize.base }}>
                            {deployment.guard?.name || deployment.guard?.guardId || '—'}
                          </div>
                          <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                            CNIC: {deployment.guard?.cnic || '—'}
                          </div>
                        </div>
                      </HistoryCardBody>
                      <HistoryCardFooter>
                        <div style={{ display: 'flex', gap: theme.spacing[3] }}>
                          <DateBadge>
                            <FiClock size={14} />
                            From: {formatDeploymentDate(deployment.postDate)}
                          </DateBadge>
                          <DateBadge>
                            <FiX size={14} />
                            To: {formatDeploymentDate(deployment.endDate)}
                          </DateBadge>
                        </div>
                      </HistoryCardFooter>
                    </HistoryCard>
                  ))}
                </div>
              </DetailGroup>
            )}

            {(!selectedClient.history || selectedClient.history.length === 0) && !selectedClient.currentDeployment && (
              <EmptyState>
                <FiUser size={48} style={{ opacity: 0.2, marginBottom: theme.spacing[3] }} />
                <EmptyStateText>No deployment history available.</EmptyStateText>
              </EmptyState>
            )}
          </div>
        )}
      </Modal>

      <DeploymentModal
        isOpen={showDeploymentModal}
        onClose={() => {
          setShowDeploymentModal(false);
          setInitialDeploymentData(null);
        }}
        clients={deploymentClients}
        clientType="Individual"
        onDeploymentSaved={() => {
          loadData();
          setShowDeploymentModal(false);
          setInitialDeploymentData(null);
        }}
        initialClientPath={initialDeploymentData?.path}
        initialSlot={initialDeploymentData?.slot}
        initialDate={initialDeploymentData?.date}
      />

      <GuardTerminationModal
        isOpen={showTerminationModal}
        onClose={() => setShowTerminationModal(false)}
        guard={terminationGuard}
        clientPath={terminationClientPath}
        onTerminated={handleTerminated}
        companyId={companyId}
      />

      {selectedClientForDocuments && (
        <GuardDocumentsModal
          isOpen={showDocumentsModal}
          onClose={() => {
            setShowDocumentsModal(false);
            setSelectedClientForDocuments(null);
          }}
          clientPath={`Individual/${companyId || auth.currentUser?.uid || ''}/clients/${selectedClientForDocuments.id}`}
          clientName={selectedClientForDocuments.name || 'Client'}
          onDocumentsUpdated={() => {
            loadData();
          }}
        />
      )}

      {/* Guard Salaries Modal (per-guard for Individual clients) */}
      <Modal
        isOpen={showSalaryModal}
        onClose={() => {
          setShowSalaryModal(false);
          setSelectedClient(null);
          setGuardSalariesEdit({});
        }}
        title={`Guard Salaries - ${selectedClient?.name || ''}`}
        footer={
          <Button variant="secondary" onClick={() => setShowSalaryModal(false)}>
            Close
          </Button>
        }
      >
        <div style={{ padding: theme.spacing[4] }}>
          <p style={{ margin: `0 0 ${theme.spacing[4]} 0`, fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
            Set monthly salary for each guard deployed at this client. These amounts are used for attendance and salary calculation (including when a guard also works at a branch).
          </p>
          {selectedClient?.deployedGuards?.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
              {selectedClient.deployedGuards.map((g: any) => (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3], flexWrap: 'wrap' }}>
                  <span style={{ minWidth: 140, fontWeight: theme.typography.fontWeight.medium }}>
                    {g.name || g.guardId}
                  </span>
                  <Input
                    type="number"
                    placeholder="Salary (Rs.)"
                    value={guardSalariesEdit[g.id]?.toString() ?? ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setGuardSalariesEdit(prev => ({ ...prev, [g.id]: isNaN(val) ? 0 : val }));
                    }}
                    dark
                  />
                  <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.muted }}>Rs./month</span>
                </div>
              ))}
              <Button
                variant="primary"
                onClick={async () => {
                  if (!selectedClient) return;
                  const clientPath = `Individual/${companyId}/clients/${selectedClient.id}`;
                  try {
                    setLoading(true);
                    await updateIndividualClient(
                      selectedClient.id,
                      { guardSalaries: guardSalariesEdit },
                      companyId
                    );
                    await updateIndividualGuardSalaries(clientPath, guardSalariesEdit, companyId);
                    toast.success('Guard salaries updated');
                    await loadData();
                    setShowSalaryModal(false);
                    setGuardSalariesEdit({});
                  } catch (error) {
                    toast.error('Failed to update guard salaries');
                  } finally {
                    setLoading(false);
                  }
                }}
                loading={loading}
                disabled={loading}
              >
                Save salaries
              </Button>
            </div>
          ) : (
            <p style={{ color: theme.colors.light.text.muted }}>No guards deployed. Deploy guards first, then set their salaries here.</p>
          )}
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default IndividualDeploymentPage;
